import React from 'react';
import { Result, Button } from 'antd';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // A stale tab (old index.html/JS still loaded) trying to lazy-import a
    // chunk that a newer deploy renamed/removed. A one-time hard reload picks
    // up the current build instead of showing the generic error screen. Guard
    // with sessionStorage so a genuinely broken/missing asset doesn't reload-loop.
    const isChunkLoadError = /Failed to fetch dynamically imported module|Loading chunk .* failed|error loading dynamically imported module/i.test(
      error?.message || ''
    );
    if (isChunkLoadError && !sessionStorage.getItem('chunk-reload-attempted')) {
      sessionStorage.setItem('chunk-reload-attempted', '1');
      window.location.reload();
      return;
    }

    // Here you could log to an error reporting service
    // logErrorToService(error, errorInfo);

    this.setState({
      hasError: true,
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false });
    // Optionally reload the page
    window.location.reload();
  };

  override componentDidMount() {
    // A render that reaches here succeeded — clear the guard so a future
    // stale-chunk error (from the NEXT deploy) can auto-reload again too.
    sessionStorage.removeItem('chunk-reload-attempted');
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <Result
            status="500"
            title="Something went wrong"
            subTitle="An unexpected error occurred while rendering this page."
            extra={
              <Button type="primary" onClick={this.handleReset}>
                Reload Page
              </Button>
            }
          />
          
          {import.meta.env.MODE === 'development' && this.state.error && (
            <div style={{ 
              marginTop: 24, 
              padding: 16, 
              background: '#f5f5f5', 
              borderRadius: 4,
              textAlign: 'left',
              overflow: 'auto'
            }}>
              <h4>Error Details (Development Mode)</h4>
              <pre style={{ fontSize: 12 }}>
                {this.state.error.stack}
              </pre>
              {this.state.errorInfo && (
                <div>
                  <h4>Component Stack</h4>
                  <pre style={{ fontSize: 12 }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}